import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from './Button';
import type { LadderWithSteps, LadderStep } from '../types/ladder';
import {
  updateStepDescription,
  createNestedLadder,
  deleteLadder,
} from '../lib/ladderUtils';

interface FullLadderViewProps {
  ladders: LadderWithSteps[];
  onLadderUpdated: () => void;
}

export function FullLadderView({
  ladders,
  onLadderUpdated,
}: FullLadderViewProps) {
  const [expandedLadders, setExpandedLadders] = useState<Set<string>>(
    new Set(ladders.filter((l) => l.level_number === 1).map((l) => l.id))
  );
  const [editingStep, setEditingStep] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [creatingNestedFor, setCreatingNestedFor] = useState<string | null>(null);
  const [nestedOutcome, setNestedOutcome] = useState('');
  const [nestedSteps, setNestedSteps] = useState(['', '', '', '', '']);

  const toggleLadder = (ladderId: string) => {
    setExpandedLadders((prev) => {
      const next = new Set(prev);
      if (next.has(ladderId)) {
        next.delete(ladderId);
      } else {
        next.add(ladderId);
      }
      return next;
    });
  };

  const startEditing = (step: LadderStep) => {
    setEditingStep(step.id);
    setEditValue(step.description);
  };

  const cancelEditing = () => {
    setEditingStep(null);
    setEditValue('');
  };

  const saveEdit = async (stepId: string) => {
    if (editValue.trim()) {
      await updateStepDescription(stepId, editValue.trim());
      onLadderUpdated();
    }
    cancelEditing();
  };

  const startCreatingNested = (stepId: string, stepDescription: string) => {
    setCreatingNestedFor(stepId);
    setNestedOutcome(stepDescription);
    setNestedSteps(['', '', '', '', '']);
  };

  const cancelCreatingNested = () => {
    setCreatingNestedFor(null);
    setNestedOutcome('');
    setNestedSteps(['', '', '', '', '']);
  };

  const handleNestedStepChange = (index: number, value: string) => {
    const newSteps = [...nestedSteps];
    newSteps[index] = value;
    setNestedSteps(newSteps);
  };

  const saveNestedLadder = async () => {
    if (!creatingNestedFor) return;

    const filledSteps = nestedSteps.filter((s) => s.trim() !== '');
    if (filledSteps.length === 0) {
      alert('Please enter at least one step');
      return;
    }

    const result = await createNestedLadder(
      creatingNestedFor,
      nestedOutcome,
      filledSteps.map((s) => s.trim())
    );

    if (result) {
      onLadderUpdated();
      cancelCreatingNested();
    }
  };

  const handleDeleteLadder = async (ladderId: string) => {
    if (confirm('Are you sure you want to delete this ladder and all its nested ladders?')) {
      await deleteLadder(ladderId);
      onLadderUpdated();
    }
  };

  const buildLadderTree = () => {
    const tree: { ladder: LadderWithSteps; children: any[] }[] = [];
    const ladderMap = new Map(ladders.map((l) => [l.id, { ladder: l, children: [] }]));

    ladders.forEach((ladder) => {
      const node = ladderMap.get(ladder.id);
      if (!node) return;

      if (ladder.parent_step_id) {
        const parentLadder = ladders.find((l) =>
          l.steps.some((s) => s.id === ladder.parent_step_id)
        );
        if (parentLadder) {
          const parentNode = ladderMap.get(parentLadder.id);
          if (parentNode) {
            parentNode.children.push(node);
          }
        }
      } else {
        tree.push(node);
      }
    });

    return tree;
  };

  const renderLadder = (
    node: { ladder: LadderWithSteps; children: any[] },
    depth: number = 0
  ) => {
    const { ladder, children } = node;
    const isExpanded = expandedLadders.has(ladder.id);
    const isCompleted = ladder.status === 'completed';
    const sortedSteps = [...ladder.steps].sort((a, b) => b.step_number - a.step_number);

    return (
      <div key={ladder.id} className={`${depth > 0 ? 'ml-6 mt-3' : 'mt-0'}`}>
        <div
          className={`border rounded-lg ${
            isCompleted ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'
          }`}
        >
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => toggleLadder(ladder.id)}
          >
            <div className="flex items-center gap-3 flex-1">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">
                  {depth === 0
                    ? `Ladder ${ladder.level_number}`
                    : `Ladder ${ladder.level_number} - ${ladder.outcome_text}`
                  }
                </h4>
                {isCompleted && (
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Completed
                  </p>
                )}
              </div>
            </div>
            {!isCompleted && depth > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLadder(ladder.id);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {isExpanded && (
            <div className="border-t border-gray-200 p-4 space-y-2">
              {sortedSteps.map((step) => {
                const hasNestedLadder = children.some(
                  (c) => c.ladder.parent_step_id === step.id
                );

                return (
                  <div key={step.id}>
                    <div
                      className={`p-3 rounded-lg border ${
                        step.is_completed
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          {editingStep === step.id ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveEdit(step.id)}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={cancelEditing}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p
                                className={`text-sm font-semibold ${
                                  step.is_completed
                                    ? 'text-gray-600 line-through'
                                    : 'text-gray-900'
                                }`}
                              >
                                {step.description}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs font-medium text-gray-500">
                                  Step {step.step_number}
                                </span>
                                {step.is_completed && (
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        {!step.is_completed && editingStep !== step.id && (
                          <div className="flex gap-2">
                            {!hasNestedLadder && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  startCreatingNested(step.id, step.description)
                                }
                              >
                                <Plus className="w-2.5 h-2.5" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditing(step)}
                            >
                              <Pencil className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {creatingNestedFor === step.id && (
                      <div className="ml-6 mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-3">
                          Create Nested Ladder
                        </h5>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Outcome
                            </label>
                            <input
                              type="text"
                              value={nestedOutcome}
                              onChange={(e) => setNestedOutcome(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Steps (5 to 1)
                            </label>
                            {[5, 4, 3, 2, 1].map((stepNum, index) => (
                              <input
                                key={stepNum}
                                type="text"
                                value={nestedSteps[index]}
                                onChange={(e) =>
                                  handleNestedStepChange(index, e.target.value)
                                }
                                placeholder={`Step ${stepNum}...`}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                              />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveNestedLadder}>
                              Create Ladder
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={cancelCreatingNested}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasNestedLadder &&
                      children
                        .filter((c) => c.ladder.parent_step_id === step.id)
                        .map((child) => renderLadder(child, depth + 1))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const tree = buildLadderTree();

  return (
    <div className="space-y-4">
      {tree.map((node) => renderLadder(node))}
    </div>
  );
}
